/**
 * Date helpers for event dates.
 *
 * Job event dates are stored as SQL DATE (no time, no zone) and arrive as ISO
 * strings pinned to midnight UTC — e.g. "2026-08-11T00:00:00.000Z". Comparing
 * those with `new Date(str) < new Date()` is wrong twice over: it treats the
 * final day of a job as already past, and because midnight UTC is mid-morning
 * in Melbourne the answer flips partway through that day.
 *
 * Compare the calendar dates as strings instead — no timezone maths, no clock.
 */

import { format } from 'date-fns';

/** "YYYY-MM-DD" for today in the viewer's local timezone. */
export function todayDateString(): string {
  // en-CA formats as YYYY-MM-DD, which sorts lexicographically.
  return new Date().toLocaleDateString('en-CA');
}

/**
 * True when `dateStr`'s calendar day is strictly before today. A job ending
 * today is NOT past — gear is still legitimately out on the last day.
 */
export function isDatePast(dateStr: string): boolean {
  return dateStr.slice(0, 10) < todayDateString();
}

/**
 * "12 Mar – 14 Mar 2026" for a multi-day job, or just "12 Mar 2026" when the
 * job starts and ends on the same day (every sales-only job, and one-day hires).
 */
export function formatJobDates(start: string, end: string): string {
  if (start.slice(0, 10) === end.slice(0, 10)) return format(new Date(start), 'd MMM yyyy');
  return `${format(new Date(start), 'd MMM')} – ${format(new Date(end), 'd MMM yyyy')}`;
}
